import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { formatShortDate } from '../../../utils/dateUtils';

const ProjectCard = ({ project, onEdit, onView, onStatusUpdate, onDelete, isSelected, onSelect }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'ongoing':
        return 'bg-accent text-accent-foreground';
      case 'completed':
        return 'bg-success text-success-foreground';
      case 'planned':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ongoing':
        return 'Play';
      case 'completed':
        return 'CheckCircle';
      case 'planned':
        return 'Clock';
      default:
        return 'Circle';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-success';
    if (progress >= 50) return 'bg-accent';
    if (progress >= 25) return 'bg-warning';
    return 'bg-error';
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 shadow-elevation-1 hover:shadow-elevation-2 transition-all duration-200 ${
      isSelected ? 'ring-2 ring-primary' : ''
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3 flex-1">
          <div className="flex-shrink-0">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelect(project?.id, e?.target?.checked)}
              className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">
              {project?.title}
            </h3>
            
            <div className="flex items-center space-x-2 mb-3">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project?.status)}`}>
                <Icon name={getStatusIcon(project?.status)} size={12} className="mr-1" />
                {project?.status === 'ongoing' ? 'جاري' : project?.status === 'completed' ? 'مكتمل' : 'مخطط'}
              </span>
              
              <span className="text-xs text-muted-foreground">
                {project?.category}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onView(project)}
            iconName="Eye"
            iconSize={16}
            title="عرض التفاصيل"
          />
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(project)}
            iconName="Edit"
            iconSize={16}
            title="تحرير المشروع"
          />
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(project)}
            iconName="Trash2"
            iconSize={16}
            title="حذف المشروع"
            className="text-error hover:text-error hover:bg-error/10"
          />
        </div>
      </div>
      {/* Project Image */}
      {project?.image && (
        <div className="mb-4 overflow-hidden rounded-lg">
          <Image
            src={project?.image}
            alt={project?.imageAlt}
            className="w-full h-32 object-cover"
          />
        </div>
      )}
      {/* Description */}
      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
        {project?.description}
      </p>
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">التقدم</span>
          <span className="text-sm text-muted-foreground">{project?.progress}%</span>
        </div>
        
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(project?.progress)}`}
            style={{ width: `${project?.progress}%` }}
          />
        </div>
      </div>
      {/* Project Details */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={14} className="text-muted-foreground" />
          <div className="text-xs">
            <p className="text-muted-foreground">تاريخ البداية</p>
            <p className="text-foreground font-medium">{formatShortDate(project?.startDate)}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Icon name="CalendarCheck" size={14} className="text-muted-foreground" />
          <div className="text-xs">
            <p className="text-muted-foreground">تاريخ الانتهاء</p>
            <p className="text-foreground font-medium">{formatShortDate(project?.endDate)}</p>
          </div>
        </div>
      </div>
      {/* Members and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon name="Users" size={14} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {project?.memberCount} عضو
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onStatusUpdate(project)}
            iconName="RefreshCw"
            iconPosition="left"
            iconSize={14}
          >
            تحديث الحالة
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;